import { useState } from 'react';
import { Screen } from 'src/utils/firebase/types';
import { firebaseLogEvent } from 'utils/firebase';

/**
 * Hook to log firebase events for modals. The modal must be conditionally rendered for events to be
 * fired properly.
 *
 * @param label - Modal name.
 *
 * @example
 * ```typescriptreact
 * const ExampleModal = () => {
 *   const [modalValue, setModalValue] = useState(0);
 *   const { logSelectorDismissedEvent, logSelectorPresentedEvent } = useSelectorEvents('example_modal');
 *
 *   useEffect(() => {
 *     logSelectorPresentedEvent(modalValue);
 *     return () => void logSelectorDismissedEvent(modalValue);
 *     // Only run once and not when values change
 *     // eslint-disable-next-line react-hooks/exhaustive-deps
 *   }, []);
 *
 *   return <div></div>;
 * };
 * ```
 */
export const useSelectorEvents = <T extends string | number>(
  /** Modal name. */
  label: Screen
) => {
  const [startTime, setStartTime] = useState(performance.now());

  const logSelectorPresentedEvent = async (value: T) => {
    setStartTime(performance.now());
    firebaseLogEvent('SelectorPresented', { label, value });
  };

  const logSelectorDismissedEvent = async (value: T) => {
    firebaseLogEvent('SelectorDismissed', { duration: performance.now() - startTime, label, value });
  };

  return {
    logSelectorDismissedEvent,
    logSelectorPresentedEvent,
  };
};
